import React from 'react';
import { Card, Space, Button, Tag, Typography, Pagination } from 'antd';
import { PhoneOutlined, EnvironmentOutlined, ClockCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Lead } from '../../../services/lead';
import './LeadCardView.css';

const { Text } = Typography;

interface LeadCardViewProps {
  leads: Lead[];
  loading: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  onView: (lead: Lead) => void;
  onAbandon?: (lead: Lead) => void;
  showActions: 'public-pool' | 'my-leads';
}

const LeadCardView: React.FC<LeadCardViewProps> = ({
  leads,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onView,
  onAbandon,
  showActions,
}) => {
  const getFollowStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      '未联系': '#d9d9d9',
      '未接通': '#faad14',
      '无效': '#ff4d4f',
      '空号': '#ff4d4f',
      '稍后联系': '#1890ff',
      '重复': '#ff7a45',
      '客诉': '#f5222d',
      '已加微信': '#52c41a',
      '已加企微': '#13c2c2',
      '邀约到所': '#722ed1',
      '已成交': '#389e0d',
    };
    return colorMap[status] || 'default';
  };

  const getUrgencyColor = (level: string) => {
    const colorMap: Record<string, string> = {
      'A类': '#ff4d4f',
      'B类': '#faad14',
      'C类': '#52c41a',
      'D类': '#1890ff',
    };
    return colorMap[level] || 'default';
  };

  const formatTime = (time?: string) => {
    if (!time) return '';
    const now = dayjs();
    const target = dayjs(time);
    const days = now.diff(target, 'day');
    
    if (days === 0) return '今天';
    if (days === 1) return '1天前';
    if (days < 7) return `${days}天前`;
    return target.format('MM-DD');
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        加载中...
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
        暂无数据
      </div>
    );
  }

  return (
    <>
      <div className="lead-card-container">
        {leads.map(lead => (
        <Card
          key={lead.id}
          className="lead-card"
          onClick={() => onView(lead)}
        >
          <div className="lead-card-header">
            <Text strong style={{ fontSize: 16 }}>
              {lead.contact_name || '未命名'}
            </Text>
            <Space size={4}>
              {lead.follow_status && (
                <Tag color={getFollowStatusColor(lead.follow_status)} style={{ margin: 0 }}>
                  {lead.follow_status}
                </Tag>
              )}
              {lead.urgency_level && (
                <Tag color={getUrgencyColor(lead.urgency_level)} style={{ margin: 0 }}>
                  {lead.urgency_level}
                </Tag>
              )}
            </Space>
          </div>

          <div className="lead-card-body">
            {lead.phone && (
              <div className="lead-card-item">
                <PhoneOutlined style={{ color: '#1890ff' }} />
                <Text>{lead.phone}</Text>
              </div>
            )}

            {(lead.province || lead.city || lead.district) && (
              <div className="lead-card-item">
                <EnvironmentOutlined style={{ color: '#52c41a' }} />
                <Text type="secondary">
                  {lead.province || ''}{lead.city || ''}{lead.district || ''}
                </Text>
              </div>
            )}

            {(lead.data_source || lead.lead_type) && (
              <div className="lead-card-item">
                <Text type="secondary">
                  {lead.data_source && `${lead.data_source}`}
                  {lead.data_source && lead.lead_type && ' · '}
                  {lead.lead_type && `${lead.lead_type}`}
                </Text>
              </div>
            )}

            {(lead.last_follow_time || lead.assigned_at) && (
              <div className="lead-card-item">
                <ClockCircleOutlined style={{ color: '#faad14' }} />
                <Text type="secondary">
                  {formatTime(lead.last_follow_time || lead.assigned_at)}
                </Text>
              </div>
            )}
          </div>

          <div className="lead-card-footer" onClick={(e) => e.stopPropagation()}>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onView(lead)}
            >
              查看
            </Button>
            {showActions === 'my-leads' && onAbandon && (
              <Button
                size="small"
                danger
                onClick={() => onAbandon(lead)}
              >
                放弃
              </Button>
            )}
          </div>
        </Card>
        ))}
      </div>

      {total && page && pageSize && onPageChange && total > pageSize && (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={onPageChange}
            showSizeChanger={false}
            simple
          />
        </div>
      )}
    </>
  );
};

export default LeadCardView;
